<template>
  <div :class="classes" class="checkbox-dropdown-input">
    <div v-if="$slots.default" class="checkbox-dropdown-input__label">
      <slot />
    </div>
    <button
      class="checkbox-dropdown-input__toggle"
      type="button"
      @click="toggleDropdown"
    >
      <TextContent :content="valueText" />
      <em v-if="valueSubtext">
        <TextContent :content="valueSubtext" />
      </em>
      <span class="checkbox-dropdown-input__icon">
        <ChevronDown />
      </span>
    </button>
    <transition name="checkbox-dropdown-input-dropdown">
      <div v-show="isDropdownOpen" class="checkbox-dropdown-input__dropdown">
        <CheckboxInput
          v-for="option in options"
          :key="option.value"
          v-model="internalValue"
          :value="option.value"
          class="checkbox-dropdown-input__option"
        >
          <TextContent :content="option.name" />
        </CheckboxInput>
      </div>
    </transition>
  </div>
</template>

<script>
import CheckboxInput from '@/components/shared/CheckboxInput';
import TextContent from '@/components/shared/TextContent';

import ChevronDown from '@/components/icons/ChevronDown';

export default {
  components: {
    CheckboxInput,
    ChevronDown,
    TextContent
  },
  props: {
    options: {
      type: Array,
      required: true,
      validator: value =>
        value.every(v => ('name' in v) && ('value' in v))
    },
    placeholder: {
      type: String,
      required: false,
      default: 'Select'
    },
    modelValue: {
      type: Array,
      required: true,
      default: () => []
    }
  },
  emits: ['update:modelValue'],
  data() {
    return {
      isDropdownOpen: false
    };
  },
  computed: {
    classes() {
      return {
        'has-open-dropdown': this.isDropdownOpen
      };
    },
    internalValue: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit('update:modelValue', value);
      }
    },
    firstSelectedOption() {
      return this.options.find(o => o.value === this.modelValue[0]);
    },
    valueText() {
      return this.firstSelectedOption?.name ?? this.placeholder;
    },
    valueSubtext() {
      return this.modelValue.length > 1 ? `(+${this.modelValue.length - 1} more)` : null;
    }
  },
  mounted() {
    document.addEventListener('click', this.handleClick);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleClick);
  },
  methods: {
    closeDropdown() {
      this.isDropdownOpen = false;
    },
    handleClick(event) {
      if (!this.$el.contains(event.target)) {
        this.closeDropdown();
      }
    },
    toggleDropdown() {
      this.isDropdownOpen = !this.isDropdownOpen;
    }
  }
};
</script>

<style lang="scss" scoped>
.checkbox-dropdown-input {
  @include font('open-sans', 'normal');
  position: relative;

  &__label {
    @include font('open-sans', 'bold');
    @include font-size('paragraph', 'sm');
    color: color('gray');
    margin-bottom: spacing('sm');
  }

  &__toggle {
    background-color: color('white');
    border: 1px solid shade('white', 7%);
    display: flex;
    height: 3rem;
    overflow: hidden;
    padding: spacing('md');
    position: relative;
    text-transform: initial;
    white-space: nowrap;
    width: 100%;

    em {
      color: color('gray');
      margin-left: spacing('xs');
    }
  }

  &__icon {
    align-items: center;
    background-color: inherit;
    bottom: 0;
    color: color('primary');
    display: flex;
    justify-content: center;
    padding: 0 spacing('md');
    pointer-events: none;
    position: absolute;
    right: 0;
    top: 0;
    transition: transform 0.2s;

    .has-open-dropdown & {
      transform: scale(-1);
    }
  }

  &__dropdown {
    background-color: color('white');
    border: 1px solid shade('white', 7%);
    border-top-width: 0;
    left: 0;
    padding: spacing('sm');
    position: absolute;
    top: 100%;
    width: 100%;
    z-index: 1;
  }

  &__option {
    &:last-child {
      margin-bottom: 0;
    }
  }

  // transitions
  &-dropdown {
    &-enter-active,
    &-leave-active {
      transition: opacity 0.2s, transform 0.2s;
    }

    &-enter,
    &-leave-to {
      opacity: 0;
      transform: translateY(-0.5rem);
    }
  }
}
</style>
